import { T, tt } from '@agos/shared'
import { Link } from 'wouter'

/**
 * What a channel's board says when the channel no longer has one - `FRONTEND.md` §9.688, owner #24 (O79).
 *
 * **Not an empty board.** Switched off for the season (*"Terasu potrebuje len cez sezónu"*), or with the flag
 * unticked, the channel's orders are on the ordinary board - and an empty board here would read as a quiet evening
 * on a terrace whose orders are in fact waiting somewhere else. So it says where they went and links there. The
 * same when the channel list could not be read: the ordinary board then draws everything, so that is where to look.
 */
export function ChannelBoardGone({
  failed,
  homeHref,
  homeLabel,
  kitchen = false,
}: {
  /** The channel list could not be read, so this board cannot tell whether it still exists. */
  failed: boolean
  /** `/kitchen` or `/restaurant`. */
  homeHref: string
  /** What the ordinary board is called, for the link. */
  homeLabel: string
  /**
   * Drawn on the kitchen board, which has its own palette rather than the app's tokens - light or dark with the
   * scheme (§9.788). Called `dark` until the board learned to be light.
   */
  kitchen?: boolean
}) {
  return (
    <div
      data-channel-board-gone={failed ? 'unread' : 'off'}
      className={
        kitchen
          ? 'm-4 rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#1e1e2e] p-6 text-slate-800 dark:text-white/80 space-y-3'
          : 'rounded-2xl border border-border bg-card p-6 space-y-3'
      }
    >
      <p className="text-sm font-semibold">
        {failed
          ? tt('Predajné kanály sa nepodarilo načítať, takže sa nedá overiť, či má tento kanál vlastnú tabuľu.')
          : tt('Tento kanál nemá zapnutú vlastnú tabuľu - jeho objednávky sú na hlavnej.')}
      </p>
      <Link href={homeHref} data-channel-board-home className="text-sm font-bold underline">
        <T>Prejsť na</T> {homeLabel}
      </Link>
    </div>
  )
}

/**
 * The ordinary board's line about a channel that has its own - §9.688.
 *
 * **A board narrowed by configuration must not read as a quiet one**, which is the rule `selectionNote` applies to a
 * narrowed filter: a cook watching an empty *Kuchyňa* while the terrace's is full has to be able to see that.
 */
export function ElsewhereNote({
  entries,
  kitchen = false,
}: {
  entries: readonly { name: string; href: string; count: number }[]
  /** On the kitchen board's own palette (§9.788), as `ChannelBoardGone`'s. */
  kitchen?: boolean
}) {
  if (entries.length === 0) return null
  return (
    <p
      data-own-boards-elsewhere={entries.length}
      className={kitchen ? 'text-[11px] text-slate-500 dark:text-white/50 space-x-3' : 'text-xs text-muted-foreground space-x-3'}
    >
      {entries.map((entry) => (
        <Link key={entry.href} href={entry.href} data-own-board-link={entry.href} className="underline">
          {entry.name} <T>má vlastnú tabuľu</T> ({entry.count})
        </Link>
      ))}
    </p>
  )
}
